import { ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { RouterLink } from '@angular/router';
import { NavigationService, SIDEBAR_SITES } from '../../core/navigation';
import {
  BrandMarkComponent,
  ButtonComponent,
  IconButtonComponent,
  NavItemComponent,
} from '../../ui';
import { UserMenuComponent } from '../shell/user-menu.component';

/**
 * Barra laterale (FR-021), dall'alto: marchio compatto, azione principale del contesto, voci
 * principali, sezione "App" (solo nel cantiere), "Cantieri" e area utente. Le voci arrivano
 * dalle dichiarazioni delle feature tramite `NavigationService`: qui non se ne scrive nessuna.
 */
@Component({
  selector: 'bs-sidebar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    BrandMarkComponent,
    ButtonComponent,
    IconButtonComponent,
    NavItemComponent,
    UserMenuComponent,
  ],
  host: { class: 'flex h-full min-h-0 flex-col' },
  template: `
    <nav
      aria-label="Navigazione principale"
      data-testid="sidebar"
      [attr.data-context]="navigation.context()"
      class="flex h-full min-h-0 flex-col gap-1 overflow-y-auto bg-stone-100 px-3.5 py-4 dark:bg-stone-900"
    >
      <div class="flex items-center justify-between gap-2 px-1 pb-2">
        <a
          routerLink="/"
          aria-label="B.Site, vai alla Dashboard"
          class="rounded-lg"
          data-testid="sidebar-brand"
        >
          <bs-brand-mark variant="compatta" [size]="17" />
        </a>
        @if (inDrawer()) {
          <bs-icon-button
            icon="close"
            label="Chiudi il menu di navigazione"
            testId="drawer-close"
            (click)="closeRequested.emit()"
          />
        }
      </div>

      @if (navigation.primaryAction(); as primary) {
        <div class="pb-2.5">
          @if (primary.link) {
            <bs-button
              size="lg"
              icon="plus"
              [fullWidth]="true"
              [link]="primary.link"
              testId="primary-action"
            >
              {{ primary.label }}
            </bs-button>
          } @else {
            <bs-button
              size="lg"
              icon="plus"
              [fullWidth]="true"
              testId="primary-action"
              (click)="primary.action?.()"
            >
              {{ primary.label }}
            </bs-button>
          }
        </div>
      }

      <ul class="flex flex-col gap-0.5" data-testid="sidebar-main">
        @for (item of navigation.mainItems(); track item.id) {
          <li>
            <bs-nav-item
              [label]="item.label"
              [icon]="item.icon"
              [appCode]="item.appCode ?? null"
              [link]="item.link"
              [exact]="item.exact ?? false"
              [badge]="item.badge?.() ?? null"
              [testId]="'nav-' + item.id"
            />
          </li>
        }
      </ul>

      @if (navigation.appItems().length > 0) {
        <h2 class="px-3 pb-1.5 pt-3.5 text-xs font-semibold text-stone-600 dark:text-stone-300">
          App
        </h2>
        <ul class="flex flex-col gap-0.5" data-testid="sidebar-apps">
          @for (item of navigation.appItems(); track item.id) {
            <li>
              <bs-nav-item
                [label]="item.label"
                [icon]="item.icon"
                [appCode]="item.appCode ?? null"
                [link]="item.link"
                [exact]="item.exact ?? false"
                [badge]="item.badge?.() ?? null"
                [testId]="'nav-' + item.id"
              />
            </li>
          }
        </ul>
      }

      @if (navigation.secondaryItems().length > 0) {
        <ul class="flex flex-col gap-0.5 pt-3.5">
          @for (item of navigation.secondaryItems(); track item.id) {
            <li>
              <bs-nav-item
                [label]="item.label"
                [icon]="item.icon"
                [link]="item.link"
                [exact]="item.exact ?? false"
                [testId]="'nav-' + item.id"
              />
            </li>
          }
        </ul>
      }

      @if (sites().length > 0) {
        <h2 class="px-3 pb-1.5 pt-3.5 text-xs font-semibold text-stone-600 dark:text-stone-300">
          Cantieri
        </h2>
        <ul class="flex flex-col gap-0.5" data-testid="sidebar-sites">
          @for (site of sites(); track site.id) {
            <li>
              <bs-nav-item
                variant="site"
                [label]="site.name"
                [link]="site.link"
                testId="sidebar-site"
              />
            </li>
          }
          <li>
            <a
              routerLink="/"
              fragment="cantieri"
              class="flex h-11 items-center rounded-full px-3 text-sm font-semibold text-stone-600 hover:bg-stone-200 hover:text-ink lg:h-9 dark:text-stone-300 dark:hover:bg-stone-800 dark:hover:text-stone-50"
              data-testid="sidebar-sites-all"
              >Vedi tutti</a
            >
          </li>
        </ul>
      }

      <div class="mt-auto pt-3">
        <bs-user-menu />
      </div>
    </nav>
  `,
})
export class SidebarComponent {
  /** Dentro il pannello mobile compare il pulsante di chiusura. */
  readonly inDrawer = input(false);
  readonly closeRequested = output();

  protected readonly navigation = inject(NavigationService);
  protected readonly sites = inject(SIDEBAR_SITES);
}
